import type { API } from "@editorjs/editorjs";
import { make } from "@/editor/utils/dom";
import React from "react";
import ReactDOM from "react-dom/client";
import AccordianConfigUi, { AccordianConfigUiRef } from "./AccordianConfigUi";
import { AccordianConfig, AccordianData, ActionConfig } from "./types";
import "./index.css";

interface Nodes {
  wrapper: HTMLElement;
  accordianContainer: HTMLElement;
  titleContainer: HTMLElement;
  contentNode: HTMLElement;
}

interface ConstructorParams {
  data: AccordianData;
  api: API;
  config: any;
  readOnly: boolean;
}

export default class Ui {
  public nodes: Nodes;
  private api: API;
  private config: AccordianConfig;
  private readOnly: boolean;
  private reactRoot: ReactDOM.Root | null = null;
  private contentRef: React.RefObject<AccordianConfigUiRef | null>;

  constructor({ data, api, config, readOnly }: ConstructorParams) {
    this.api = api;
    this.config = config;
    this.readOnly = readOnly;
    this.contentRef = React.createRef<AccordianConfigUiRef>();

    // Create the content node container
    const contentNodeContainer = make("div", ["content-editor-container"]);

    this.nodes = {
      wrapper: make("div", [this.CSS.baseClass, this.CSS.wrapper]),
      accordianContainer: make("div", [this.CSS.accordianContainer]),
      titleContainer: make("h2", [this.CSS.titleContainer], {
        contentEditable: (!this.readOnly).toString(),
      }),
      contentNode: contentNodeContainer,
    };

    // Set initial title
    this.nodes.titleContainer.innerHTML =
      data?.title || this.config?.title || "Accordian Title";

    // Mount React component
    this.mountReactComponent(data);

    // Build the DOM structure
    this.nodes.wrapper.appendChild(this.nodes.titleContainer);
    this.nodes.wrapper.appendChild(this.nodes.contentNode);
  }

  private mountReactComponent(data: AccordianData) {
    if (!this.reactRoot) {
      this.reactRoot = ReactDOM.createRoot(this.nodes.contentNode);
    }

    this.reactRoot.render(
      React.createElement(AccordianConfigUi, {
        ref: this.contentRef,
        contentData: data?.contentData,
        imageUploadService: this.config.uploadService,
      })
    );
  }

  public render(): HTMLElement {
    return this.nodes.wrapper;
  }

  public renderSettings(data: AccordianData): HTMLElement {
    const settingsWrapper = make("div", [this.CSS.renderSettingContainer]);

    this.renderSettingsConfig().forEach((actionConfig) => {
      const button = make("button", [this.CSS.renderSettingButton], {
        buttonId: actionConfig.name,
      });
      button.innerHTML = actionConfig.icon;
      const buttonTitle = make("span");
      buttonTitle.innerText = actionConfig.title;
      button.appendChild(buttonTitle);
      button.addEventListener("click", () => {
        actionConfig?.action?.();
      });
      if (data.showTitle && actionConfig.toggle) {
        button.classList.add("active");
      }
      settingsWrapper.appendChild(button);
    });

    return settingsWrapper;
  }

  private renderSettingsConfig(): ActionConfig[] {
    return [
      {
        name: "showTitle",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-captions-off-icon lucide-captions-off"><path d="M10.5 5H19a2 2 0 0 1 2 2v8.5"/><path d="M17 11h-.5"/><path d="M19 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2"/><path d="m2 2 20 20"/><path d="M7 11h4"/><path d="M7 15h2.5"/></svg>`,
        title: "Show Title",
        toggle: true,
        action: () => {
          const button = document.querySelector("button[buttonId='showTitle']");
          if (button) {
            button.classList.toggle("active");
          }
          this.handleHeadingChanges(
            this.nodes.titleContainer.style.visibility === "hidden"
          );
        },
      },
    ];
  }

  public tune({ showTitle = false }: { showTitle?: boolean }) {
    this.handleHeadingChanges(showTitle);
  }

  private handleHeadingChanges = (showTitle = true) => {
    this.nodes.titleContainer.style.cssText = `visibility: ${
      showTitle ? "visible" : "hidden"
    }; margin-bottom: ${showTitle ? "0px" : "-40px"};`;
  };

  public get getUiData() {
    // Get content data from React component via ref
    const contentData = this.contentRef.current?.getContentData?.() || [];

    return {
      title: this.nodes.titleContainer.innerHTML,
      showTitle: this.nodes.titleContainer.style.visibility !== "hidden",
      contentData: contentData,
    };
  }

  private get CSS(): Record<string, string> {
    return {
      baseClass: this.api.styles.block,
      wrapper: "accordian-tool",
      accordianContainer: "accordian_tool__container",
      titleContainer: "accordian_tool__title-container",
      renderSettingContainer: "accordian-tool-settings",
      renderSettingButton: "accordian-tool-setting-button",
    };
  }

  static get toolBoxIcon(): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 96 120" x="0px" y="0px"><path d="M16.004 86c-0.808 0-1.536-0.484-1.848-1.236s-0.14-1.6 0.432-2.18c2.212-2.22 6.208-7.66 8.88-13.52-13.296-5.82-21.468-16.056-21.468-27.064 0-17.648 20.636-32 46-32s46 14.352 46 32c0 17.64-20.636 32-46 32-1.644 0-3.256-0.084-4.852-0.204-7.528 8.096-16.656 12.204-27.144 12.204zM48.004 14c-23.16 0-42 12.572-42 28 0 9.844 7.984 19.088 20.832 24.132 0.496 0.196 0.896 0.584 1.104 1.080 0.208 0.488 0.22 1.048 0.020 1.536-1.864 4.652-4.756 9.424-7.396 12.948 7.716-1.060 14.52-4.844 20.276-11.304 0.42-0.468 1.028-0.716 1.668-0.656 1.8 0.156 3.628 0.264 5.492 0.264 23.16 0 42-12.564 42-28s-18.836-28-41.996-28z" fill="#000000"/><path d="M46.004 54c-1.104 0-2-0.896-2-2v-6.252c0-1.1 0.896-2 2-2 6.424 0 10.752-3.992 10.752-9.936 0-5.752-3.896-9.928-9.252-9.928-5.592 0-10.5 4.736-10.5 10.116 0 1.104-0.896 2-2 2s-2-0.896-2-2c0-7.656 6.64-14.116 14.5-14.116 7.56 0 13.252 5.992 13.252 13.928 0 7.572-5.148 13.024-12.752 13.836v4.352c0 1.104-0.896 2-2 2z" fill="#000000"/><path d="M46.004 66c-1.104 0-2-0.896-2-2v-4c0-1.096 0.896-2 2-2s2 0.904 2 2v4c0 1.104-0.896 2-2 2z" fill="#000000"/></svg>`;
  }

  // Clean up React component when destroyed
  public destroy() {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }
  }
}
